import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import type DealStage from "@/model/interface/DealStage";
import { stageCategoryConverts } from "@/enum/DealStageCategory";
import { useDeleteDealStageMutation, useGetDealStageUsageQuery } from "@/store/rest_api";
import { apiErrorMessage } from "@/util/apiError";

interface DeleteDealStageDialogProps {
  stage: DealStage | null;
  stages: DealStage[];
  onClose: () => void;
}

const DeleteDealStageDialog: React.FC<DeleteDealStageDialogProps> = ({ stage, stages, onClose }) => {
  const { data: usage, isFetching } = useGetDealStageUsageQuery(stage?.id ?? "", {
    skip: !stage,
    refetchOnMountOrArgChange: true,
  });
  const [deleteDealStage, { isLoading: isDeleting }] = useDeleteDealStageMutation();
  const [targetStageId, setTargetStageId] = useState("");

  const otherStages = stages.filter((candidate) => candidate.id !== stage?.id);
  const dealCount = usage?.dealCount ?? 0;
  const needsTarget = dealCount > 0;
  const usageLoaded = !!usage && !isFetching;
  const targetStage = otherStages.find((candidate) => candidate.id === targetStageId);
  const movingIntoConversion =
    !!targetStage && stageCategoryConverts(targetStage.category) && !stageCategoryConverts(stage?.category);

  const handleDelete = async () => {
    if (!stage) {
      return;
    }
    try {
      await deleteDealStage({ id: stage.id, moveDealsTo: needsTarget ? targetStageId : undefined }).unwrap();
      toast.success(`${stage.name} removed`);
      onClose();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not remove the stage"));
    }
  };

  useEffect(() => {
    setTargetStageId("");
  }, [stage?.id]);

  return (
    <AlertDialog
      open={!!stage}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove {stage?.name}</AlertDialogTitle>
          <AlertDialogDescription>
            The stage leaves the pipeline and every stage picker. Deal history keeps its name.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {!usageLoaded ? (
          <Skeleton className="h-16 w-full" />
        ) : (
          <div className="space-y-4 text-sm">
            {needsTarget && (
              <div className="space-y-2">
                <p>
                  <span className="font-medium tabular-nums">{dealCount}</span>{" "}
                  {dealCount === 1 ? "deal sits in this stage. Move it to:" : "deals sit in this stage. Move them to:"}
                </p>
                <Select value={targetStageId} onValueChange={setTargetStageId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Pick a stage" />
                  </SelectTrigger>
                  <SelectContent>
                    {otherStages.map((candidate) => (
                      <SelectItem key={candidate.id} value={candidate.id}>
                        <span className="flex items-center gap-2">
                          <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: candidate.color }} />
                          {candidate.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {movingIntoConversion && (
                  <p className="text-xs text-info">Leads whose deals move there become contacts.</p>
                )}
              </div>
            )}
            {usage.dispositions.length > 0 && (
              <div className="space-y-1">
                <p className="font-medium">Dispositions that move deals here</p>
                <p className="text-muted-foreground">
                  {usage.dispositions.map((reference) => reference.name).join(", ")}. They stop moving deals once the
                  stage is removed.
                </p>
              </div>
            )}
            {usage.workflows.length > 0 && (
              <div className="space-y-1">
                <p className="font-medium">Workflows that move deals here</p>
                <p className="text-muted-foreground">
                  {usage.workflows.map((reference) => reference.name).join(", ")}. Their Move deal stage step fails
                  until you pick another stage in the workflow.
                </p>
              </div>
            )}
            {!needsTarget && usage.dispositions.length === 0 && usage.workflows.length === 0 && (
              <p className="text-muted-foreground">No deals, dispositions or workflows use this stage.</p>
            )}
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={() => void handleDelete()}
            disabled={isDeleting || !usageLoaded || (needsTarget && !targetStageId)}
          >
            Remove stage
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteDealStageDialog;
